/**
 * Polling-unit cancellation (STORY-71 v2) — the api.core contract as the admin app reads it.
 *
 * Enums travel BY NAME. Every field added by v2 is optional here on purpose: a v1 server
 * answers rows without `source`, `reviewStatus`, `state` or `evidenceCount`, and a missing
 * field is "unknown" — never `false`, never `0` (see `docs/claude/backend-contracts.md`).
 */

export type CancellationReason = 'OverVoting' | 'Violence' | 'BvasFailure' | 'MaterialsNotDelivered' | 'CourtOrder' | 'Other';
export type CancellationSource = 'Admin' | 'Agent';
export type CancellationReviewStatus = 'Unreviewed' | 'Confirmed';
export type CancellationState = 'Active' | 'Withdrawn' | 'Reinstated';
export type EvidenceKind = 'Image' | 'Video' | 'Audio';
export type CancellationChangeKind = 'Recorded' | 'Withdrawn' | 'Reviewed' | 'Reinstated' | 'EvidenceAdded' | 'IncidentLinked';

export const CANCELLATION_REASONS: CancellationReason[] = [
	'Violence',
	'OverVoting',
	'BvasFailure',
	'MaterialsNotDelivered',
	'CourtOrder',
	'Other',
];

export interface ICancellationRow {
	id: number;
	electionId: number;
	partyId: number;
	pollingUnitId?: number;
	pollingUnitCode: string;
	pollingUnitName?: string | null;
	wardId?: number;
	wardName?: string | null;
	wardCode?: string | null;
	lgaId?: number;
	lgaName?: string | null;
	lgaCode?: string | null;
	reason: CancellationReason | string;
	notes?: string | null;
	cancelledAt?: string | null;
	recordedAt?: string | null;
	recordedBy?: string | null;
	recordedByUserId?: number | null;
	source?: CancellationSource;
	reviewStatus?: CancellationReviewStatus;
	reviewedBy?: string | null;
	reviewedAt?: string | null;
	state?: CancellationState;
	stateChangedAt?: string | null;
	stateChangedBy?: string | null;
	stateChangedByUserId?: number | null;
	stateReason?: string | null;
	withdrawableUntil?: string | null;
	latitude?: number | string | null;
	longitude?: number | string | null;
	accuracyMeters?: number | null;
	clientReferenceId?: string | null;
	vesrIncidentId?: string | null;
	evidenceCount?: number;
	/**
	 * Uploads the agent's phone started that never registered — abandoned sessions included, and it
	 * never decreases, so it is not "still uploading". Optional: older api.core builds do not send it.
	 */
	pendingEvidenceCount?: number;
	/** Increases on every change of the row — the same counter `CancellationChanged` carries. */
	version?: number;
}

export interface ICancellationPage {
	rows: ICancellationRow[];
	totalCount: number | undefined;
	pageNumber: number;
	pageSize: number;
}

/** `GET capabilities`. `version: 'v1'` is synthesised client-side when the route 404s. */
export interface ICancellationCapabilities {
	version: 'v1' | 'v2';
	agentReport: boolean;
	withdrawWindowMinutes: number | null;
	review: boolean;
	evidence: boolean;
	impact: boolean;
	liveEvents: boolean;
	candidateSms: boolean;
	resultsCancellationAware: boolean;
	/** The list honours `vesrIncidentId`. Without it the param is ignored, so callers re-filter. */
	incidentLookup: boolean;
}

export const BASELINE_CAPABILITIES: ICancellationCapabilities = {
	version: 'v1',
	agentReport: false,
	withdrawWindowMinutes: null,
	review: false,
	evidence: false,
	impact: false,
	liveEvents: false,
	candidateSms: false,
	resultsCancellationAware: false,
	incidentLookup: false,
};

export type RegisteredSource = 'VOTER_REGISTER' | 'POLLING_UNIT_LEGACY' | 'NOT_PUBLISHED';

export interface ICancellationImpact {
	pollingUnitCode: string;
	/** null when not captured — never 0-as-unknown. */
	registeredVoters: number | null;
	registeredSource?: RegisteredSource;
	resultsSubmitted?: { source: string; uploaded: boolean }[];
	anyResultSubmitted?: boolean;
}

export interface ICancellationImpactSummary {
	units: number;
	unitsUnreviewed?: number;
	registeredVoters: number | null;
	registeredCapturedUnits?: number;
	unitsWithResults?: number;
}

export interface ICancellationEvidence {
	id: number;
	kind: EvidenceKind;
	mime?: string;
	bytes?: number;
	durationSec?: number | null;
	capturedAt?: string | null;
	uploadedBy?: string | null;
	uploadedAt?: string | null;
	viewUrl: string;
	thumbnailUrl?: string | null;
	viewUrlExpiresAt?: string | null;
	/** `false` for a file kept on the retired media service — on the record, but it cannot be shown (`viewUrl` is empty). */
	playable?: boolean;
	/**
	 * `viewUrl` with api.core's attachment mode on (`?download=1`). Present only when the server
	 * honours it — absence means "offer no download", never "build one". Shares `viewUrlExpiresAt`.
	 */
	downloadUrl?: string | null;
}

export type AlertChannel = 'Sms' | 'WhatsApp';

export interface ICancellationAlertSettings {
	candidateSms: boolean;
	channel: AlertChannel;
	recipientUserIds: number[];
}

export interface IBulkCancellationResult {
	recorded: ICancellationRow[];
	alreadyCancelled: string[];
	notInElection: string[];
	unknown: string[];
}

/** `CancellationChanged` on the Live Election hub. */
export interface ICancellationChangedEvent {
	id: number;
	version: number;
	changeKind: CancellationChangeKind | string;
	row: ICancellationRow;
}

export interface ICancellationFilters {
	lgaId: number | null;
	wardId: number | null;
	reason: CancellationReason | null;
	reviewStatus: CancellationReviewStatus | null;
	source: CancellationSource | null;
	/** Default Active; `All` asks the server for every state. */
	state: CancellationState | 'All';
	pageNumber: number;
}
